import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { GlassCard, GradientButton, useAlert } from '../../components';
import { logout } from '../../services/authService';
import { formatCurrency } from '../../utils/helpers';
import './ProfileScreen.css';

export const ProfileScreen = () => {
    const navigate = useNavigate();
    const { userData } = useAuth();
    const alert = useAlert();
    const [isEditingPhone, setIsEditingPhone] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState(userData?.phoneNumber || '');
    const [loading, setLoading] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isInstalled, setIsInstalled] = useState(false);

    useEffect(() => {
        // Check if already installed
        if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
            setIsInstalled(true);
        }
    }, []);

    const handleInstallClick = async () => {
        const promptEvent = window.deferredPrompt;

        if (!promptEvent) {
            // Check if already installed
            if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
                alert.showInfo('Installed', 'Yonex AI is already installed on your device!');
                setIsInstalled(true);
            } else {
                alert.showInfo('Preparing', 'Preparing installation... Please try again in a few seconds or use the "Install App" option in your browser menu.');
            }
            return;
        }

        // Show the native install prompt
        promptEvent.prompt();

        // Wait for the user to respond to the prompt
        const { outcome } = await promptEvent.userChoice;
        if (outcome === 'accepted') {
            console.log('User accepted the install prompt');
            window.deferredPrompt = null;
            setIsInstalled(true);
            alert.showSuccess('Success', 'Thank you for installing Yonex AI!');
        } else {
            console.log('User dismissed the install prompt');
        }
    };

    useEffect(() => {
        if (userData?.phoneNumber) {
            setPhoneNumber(userData.phoneNumber);
        }
    }, [userData]);

    const menuItems = [
        { icon: 'account_balance', label: 'Bank Details', route: '/bank-details', color: '#3B82F6' },
        { icon: 'lock', label: 'Security', route: '/security', color: '#D4AF37' },
        { icon: 'group', label: 'My Team', route: '/referral', color: '#00C853' },
        { icon: 'history', label: 'Transaction History', route: '/transactions', color: '#8B5CF6' },
        { icon: 'help_outline', label: 'Support', route: '/support', color: '#F59E0B' },
        { icon: 'info', label: 'About', route: '/about', color: '#6B7280' },
        {
            icon: 'add_to_home_screen',
            label: 'Add to Home Screen',
            action: 'install',
            color: '#10B981',
            hide: isInstalled
        },
    ];

    const handleUpdatePhone = async () => {
        if (!phoneNumber) {
            alert.showError('Error', 'Please enter a valid mobile number');
            return;
        }

        if (phoneNumber.length < 10) {
            alert.showError('Error', 'Mobile number should be at least 10 digits');
            return;
        }

        setLoading(true);
        try {
            const { updateUserProfile } = await import('../../services/firestoreService');
            await updateUserProfile({ phoneNumber });
            alert.showSuccess('Success', 'Mobile number updated successfully');
            setIsEditingPhone(false);
        } catch (error) {
            alert.showError('Error', 'Failed to update mobile number');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        alert.showConfirm(
            'Logout',
            'Are you sure you want to logout?',
            async () => {
                try {
                    await logout();
                    navigate('/login');
                } catch (error) {
                    alert.showError('Error', 'Failed to logout');
                }
            }
        );
    };

    return (
        <div className="profile-screen">
            {/* Profile Header */}
            <div className="profile-header">
                <div className="avatar-container">
                    {userData?.photoURL ? (
                        <img src={userData.photoURL} alt="Profile" className="avatar-image" />
                    ) : (
                        <span className="material-icons avatar-icon">person</span>
                    )}
                </div>
                <h2 className="profile-name">{userData?.displayName || 'User'}</h2>
                <p className="profile-email">{userData?.email}</p>
                <div className="profile-referral">
                    <span className="material-icons">qr_code_2</span>
                    <span>Referral Code: {userData?.referralCode || 'N/A'}</span>
                </div>
                <div className="profile-phone" onClick={() => setIsEditingPhone(true)}>
                    <span className="material-icons">phone</span>
                    <span>{userData?.phoneNumber || 'Add Mobile Number'}</span>
                    <button className="edit-phone-btn">
                        <span className="material-icons">edit</span>
                    </button>
                </div>
            </div>

            {/* Balance Summary */}
            <GlassCard className="balance-summary">
                <div className="balance-item">
                    <span className="balance-label">Total Balance</span>
                    <span className="balance-value total">
                        {formatCurrency((userData?.rechargeBalance || 0) + (userData?.withdrawBalance || 0))}
                    </span>
                </div>
                <div className="balance-divider"></div>
                <div className="balance-item">
                    <span className="balance-label">Recharge</span>
                    <span className="balance-value">{formatCurrency(userData?.rechargeBalance || 0)}</span>
                </div>
                <div className="balance-divider"></div>
                <div className="balance-item">
                    <span className="balance-label">Withdraw</span>
                    <span className="balance-value">{formatCurrency(userData?.withdrawBalance || 0)}</span>
                </div>
            </GlassCard>

            {/* Menu Items */}
            <div className="menu-section">
                {menuItems.filter(item => !item.hide).map((item, index) => (
                    <GlassCard
                        key={index}
                        className="menu-item"
                        onClick={() => item.action === 'install' ? handleInstallClick() : navigate(item.route)}
                    >
                        <div
                            className="menu-icon-container"
                            style={{ backgroundColor: `${item.color}15` }}
                        >
                            <span
                                className="material-icons"
                                style={{ color: item.color }}
                            >
                                {item.icon}
                            </span>
                        </div>
                        <span className="menu-label">{item.label}</span>
                        <span className="material-icons menu-arrow">chevron_right</span>
                    </GlassCard>
                ))}
            </div>



            {/* Logout Button */}
            <GradientButton
                title="Logout"
                onClick={handleLogout}
                variant="outline"
                icon={<span className="material-icons">logout</span>}
            />

            {/* App Version */}
            <p className="app-version">Yonex AI v1.0.0</p>

            {/* Edit Phone Modal - Using basic HTML/CSS if no ready Modal component, but I'll use a local implementation */}
            {isEditingPhone && (
                <div className="modal-overlay">
                    <GlassCard className="edit-phone-modal">
                        <div className="modal-header">
                            <h3>Update Mobile Number</h3>
                            <button className="close-btn" onClick={() => setIsEditingPhone(false)}>
                                <span className="material-icons">close</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="input-group">
                                <span className="material-icons">phone</span>
                                <input
                                    type="tel"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                                    placeholder="Enter 10-digit mobile number"
                                    maxLength={10}
                                />
                            </div>
                            <GradientButton
                                title={loading ? 'Updating...' : 'Save Changes'}
                                onClick={handleUpdatePhone}
                                disabled={loading || phoneNumber === userData?.phoneNumber}
                            />
                        </div>
                    </GlassCard>
                </div>
            )}
        </div>
    );
};

export default ProfileScreen;
